import { Plate } from '../components/Card';
import React, { useEffect, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from 'recharts';
import { getOnecPing, getOnecDebts, getOnecPayroll, getOnecMargin, refreshOnecCache, getDashboardPriority, getDashboardStock, getFinDirectorSalesReport, getFinDirectorWriteoffReport, exportFinDirectorWriteoffReportXlsx, getFinDirectorFinanceSummary, getDeadStockList, assignDeadStock, markDeadStockSold, getDeadStockProgress, cancelDeadStockTask, getProcurementReceipts, getProcurementReceiptDetail, exportProcurementReceiptXlsx } from '../api';

const fmt  = n => n ? n.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽' : '0 ₽';
const fmtN = n => n ? n.toLocaleString('ru-RU', { maximumFractionDigits: 1 }) : '0';
const fmtPct = n => `${Number(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
const fmtX = n => `${Number(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}x`;
const fmtMaybeMoney = (n) => (n === null || n === undefined ? 'н/д' : fmt(n));
const fmtMaybePct = (n) => (n === null || n === undefined ? 'н/д' : fmtPct(n));
const fmtDate = (raw) => {
  if (!raw) return '—';
  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('ru-RU');
};

const debugLog = (runId, hypothesisId, location, message, data = {}) => {
  // #region agent log
  fetch('http://127.0.0.1:7714/ingest/438ca71a-251c-44c8-9452-87087ef7113a',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'1ce0d7'},body:JSON.stringify({sessionId:'1ce0d7',runId,hypothesisId,location,message,data,timestamp:Date.now()})}).catch(()=>{});
  // #endregion
};

// Цвета модуля Финдиректор: фирменный голубой
const KA_GOLD = '#00a7dc';
const KA_GOLD2 = '#00a7dc';
const KA_BLUE = '#00a7dc';

const ACard = ({ children, style, ...props }) => (
  <div style={{
    background: 'var(--bg2)',
    borderRadius: 12,
    border: '1px solid var(--border)',
    padding: '20px 24px',
    ...style,
  }} {...props}>
    {children}
  </div>
);

const Metric = ({ title, value, sub, color, icon }) => (
  <div style={{
    background: 'var(--bg3)',
    borderRadius: 14,
    padding: '16px 18px 18px',
    flex: 1,
    minWidth: 160,
  }}>
    <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon && <span>{icon}</span>}
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
    </div>
    <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{sub}</div>}
  </div>
);

export default function FinDirector() {
  // ── Мета / период ─────────────────────────────────────────────────────────
  const [connected, setConnected] = useState(null);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [refreshing, setRefreshing] = useState(false);

  // ── Долги / ФОТ ───────────────────────────────────────────────────────────
  const [debts, setDebts] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [debtTab, setDebtTab] = useState('all');

  // ── Приоритетные метрики (сегодня + текущий месяц) ────────────────────────
  const [priorityData, setPriorityData] = useState(null);
  const [priorityLoading, setPriorityLoading] = useState(true);

  // ── Остатки склада (обогащённые ценами) ────────────────────────────────────
  const [stockData, setStockData] = useState(null);
  const [stockLoading, setStockLoading] = useState(true);
  const [stockSearch, setStockSearch] = useState('');

  // ── Полная историческая маржа (12 мес, по дням, топ товаров) ─────────────
  const [margin, setMargin] = useState(null);
  const [marginLoading, setMarginLoading] = useState(true);

  // ── Навигация по дням ─────────────────────────────────────────────────────
  const [dayIdx, setDayIdx] = useState(-1);
  const [dayFilter, setDayFilter] = useState('');

  // ── Сортировка таблицы остатков ───────────────────────────────────────────
  const [stockSort, setStockSort] = useState({ key: 'qty', dir: 'desc' });

  // ── Отчёт о продажах ─────────────────────────────────────────────────────
  const [salesPeriod, setSalesPeriod] = useState('week');
  const [salesDateFrom, setSalesDateFrom] = useState('');
  const [salesDateTo, setSalesDateTo] = useState('');
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesSearch, setSalesSearch] = useState('');
  const [excludeOwnRetail, setExcludeOwnRetail] = useState(false);
  const [writeoffPeriod, setWriteoffPeriod] = useState('month');
  const [writeoffDateFrom, setWriteoffDateFrom] = useState('');
  const [writeoffDateTo, setWriteoffDateTo] = useState('');
  const [writeoffData, setWriteoffData] = useState(null);
  const [writeoffLoading, setWriteoffLoading] = useState(false);
  const [writeoffSearch, setWriteoffSearch] = useState('');
  const [writeoffSort, setWriteoffSort] = useState({ key: 'cost_total', dir: 'desc' });
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [expandedReceiptId, setExpandedReceiptId] = useState('');
  const [receiptDetails, setReceiptDetails] = useState({});
  const [receiptDetailLoading, setReceiptDetailLoading] = useState({});
  const [financeData, setFinanceData] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);

  // ── Неликвид: управление ────────────────────────────────────────────────
  const [deadStock, setDeadStock] = useState(null);
  const [deadStockLoading, setDeadStockLoading] = useState(false);
  const [deadStockProgress, setDeadStockProgress] = useState(null);
  const [dsSearch, setDsSearch] = useState('');
  const [dsAssigning, setDsAssigning] = useState(null); // sku being assigned
  const [dsManager, setDsManager] = useState('');
  const [dsBonusPct, setDsBonusPct] = useState(5);
  const [dsSelling, setDsSelling] = useState(null); // task_id being sold
  const [dsSoldQty, setDsSoldQty] = useState('');
  const [dsSoldAmount, setDsSoldAmount] = useState('');

  // ref для авто-обновления priority каждые 30с
  const priorityTimerRef = useRef(null);
  const receiptAutoOpenRef = useRef('');

  // ── Функции загрузки ──────────────────────────────────────────────────────

  const loadPriority = () => {
    setPriorityLoading(true);
    getDashboardPriority()
      .then(d => { setPriorityData(d); setPriorityLoading(false); })
      .catch(() => { setPriorityLoading(false); });
  };

  const loadStock = () => {
    setStockLoading(true);
    getDashboardStock()
      .then(d => { setStockData(d); setStockLoading(false); })
      .catch(e => { setStockData({ loading: false, items: [], totals: {}, error: e.message }); setStockLoading(false); });
  };

  const loadMargin = (p) => {
    setMarginLoading(true);
    getOnecMargin(p)
      .then(d => { setMargin(d); setMarginLoading(false); })
      .catch(e => { setMargin({ monthly: [], current: {}, top_items: [], daily: [], error: e.message }); setMarginLoading(false); });
  };

  const loadSalesReport = (p, dFrom, dTo, exclOwn) => {
    setSalesLoading(true);
    setSalesData(null);
    const period  = p || salesPeriod;
    const dateFrom = dFrom !== undefined ? dFrom : salesDateFrom;
    const dateTo   = dTo   !== undefined ? dTo   : salesDateTo;
    const excl     = exclOwn !== undefined ? exclOwn : excludeOwnRetail;
    getFinDirectorSalesReport(period, dateFrom, dateTo, excl)
      .then(d => { setSalesData(d); setSalesLoading(false); })
      .catch(e => { setSalesData({ error: e.response?.data?.detail || e.message, items: [] }); setSalesLoading(false); });
  };

  const loadWriteoffReport = (p, dFrom, dTo) => {
    setWriteoffLoading(true);
    const targetPeriod = p || writeoffPeriod;
    const targetDateFrom = dFrom !== undefined ? dFrom : writeoffDateFrom;
    const targetDateTo = dTo !== undefined ? dTo : writeoffDateTo;
    getFinDirectorWriteoffReport(targetPeriod, targetDateFrom, targetDateTo)
      .then(d => { setWriteoffData(d); setWriteoffLoading(false); })
      .catch(e => {
        setWriteoffData({ error: e.response?.data?.detail || e.message, items: [] });
        setWriteoffLoading(false);
      });
  };

  const loadReceipts = () => {
    setReceiptsLoading(true);
    debugLog('initial', 'H4', 'Academy.js:178', 'loadReceipts start', {
      expandedReceiptId,
      pathname: window.location.pathname,
      hasReceiptQuery: window.location.search.includes('receipt='),
    });
    getProcurementReceipts()
      .then(d => {
        debugLog('initial', 'H4', 'Academy.js:184', 'loadReceipts success', {
          count: Array.isArray(d) ? d.length : -1,
          firstDocNumber: Array.isArray(d) && d[0] ? (d[0].doc_number || '') : '',
        });
        setReceipts(d || []);
        setReceiptsLoading(false);
      })
      .catch((e) => {
        debugLog('initial', 'H4', 'Academy.js:192', 'loadReceipts failed', {
          message: e?.message || '',
          status: e?.response?.status || 0,
        });
        setReceipts([]);
        setReceiptsLoading(false);
      });
  };

  const syncReceiptUrl = (receiptId = '') => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (receiptId) url.searchParams.set('receipt', receiptId);
    else url.searchParams.delete('receipt');
    url.hash = 'receipts';
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const loadReceiptDetail = async (receiptId) => {
    if (receiptDetails[receiptId]?.items) return receiptDetails[receiptId];
    setReceiptDetailLoading(prev => ({ ...prev, [receiptId]: true }));
    try {
      const detail = await getProcurementReceiptDetail(receiptId);
      setReceiptDetails(prev => ({ ...prev, [receiptId]: detail }));
      return detail;
    } finally {
      setReceiptDetailLoading(prev => ({ ...prev, [receiptId]: false }));
    }
  };

  const handleToggleReceipt = async (receiptId) => {
    if (expandedReceiptId === receiptId) {
      setExpandedReceiptId('');
      syncReceiptUrl('');
      return;
    }
    setExpandedReceiptId(receiptId);
    syncReceiptUrl(receiptId);
    await loadReceiptDetail(receiptId);
  };

  const handleShareReceipt = async (receiptId) => {
    const shareUrl = `${window.location.origin}/findirector?receipt=${encodeURIComponent(receiptId)}#receipts`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Ссылка на документ приёмки скопирована');
    } catch (e) {
      window.prompt('Скопируйте ссылку на документ приёмки', shareUrl);
    }
  };

  const loadFinance = (p) => {
    const targetPeriod = p || period;
    setFinanceLoading(true);
    getFinDirectorFinanceSummary(targetPeriod)
      .then(d => { setFinanceData(d); setFinanceLoading(false); })
      .catch(e => {
        setFinanceData({ error: e.response?.data?.detail || e.message, loading: false });
        setFinanceLoading(false);
      });
  };

  const loadDeadStock = () => {
    setDeadStockLoading(true);
    getDeadStockList()
      .then(d => { setDeadStock(d); setDeadStockLoading(false); })
      .catch(() => setDeadStockLoading(false));
    getDeadStockProgress()
      .then(setDeadStockProgress)
      .catch(() => {});
  };

  // ── Начальная загрузка (только один раз при монтировании) ─────────────────
  useEffect(() => {
    debugLog('initial', 'H3', 'Academy.js:260', 'findirector mounted', {
      pathname: window.location.pathname,
      search: window.location.search,
    });
    getOnecPing().then(res => setConnected(res.ok)).catch(() => setConnected(false));
    getOnecDebts().then(setDebts).catch(() => {});
    getOnecPayroll(period).then(setPayroll).catch(() => {});
    loadPriority();
    loadStock();
    loadMargin(period);
    loadFinance(period);
    loadDeadStock();
    loadWriteoffReport('month', '', '');
    loadReceipts();
  }, []);

  // ── При смене периода — только маржа и зарплата ───────────────────────────
  useEffect(() => {
    loadMargin(period);
    getOnecPayroll(period).then(setPayroll).catch(() => {});
    loadFinance(period);
  }, [period]);

  useEffect(() => {
    if (!receipts.length || typeof window === 'undefined') return;
    const targetId = new URLSearchParams(window.location.search).get('receipt');
    if (!targetId || receiptAutoOpenRef.current === targetId) return;
    if (!receipts.some(r => String(r.id) === String(targetId))) return;
    receiptAutoOpenRef.current = targetId;
    setExpandedReceiptId(targetId);
    loadReceiptDetail(targetId).finally(() => {
      document.getElementById('receipts')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [receipts]);

  // ── Автополлинг priority пока данные загружаются, потом каждые 5 мин ─────
  useEffect(() => {
    if (priorityTimerRef.current) clearInterval(priorityTimerRef.current);
    const interval = priorityData?.loading ? 30000 : 5 * 60 * 1000;
    priorityTimerRef.current = setInterval(() => {
      getDashboardPriority()
        .then(d => { setPriorityData(d); if (!d.loading) setPriorityLoading(false); })
        .catch(() => {});
    }, interval);
    return () => clearInterval(priorityTimerRef.current);
  }, [priorityData?.loading]);

  // ── Автополлинг stock пока цены загружаются ───────────────────────────────
  useEffect(() => {
    if (!stockData?.prices_loading) return;
    const t = setInterval(() => {
      getDashboardStock()
        .then(d => { setStockData(d); if (!d.prices_loading) setStockLoading(false); })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [stockData?.prices_loading]);

  // ── Автополлинг margin пока loading ──────────────────────────────────────
  useEffect(() => {
    if (!margin?.loading) return;
    const t = setInterval(() => {
      getOnecMargin(period)
        .then(d => { setMargin(d); if (!d.loading) setMarginLoading(false); })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [margin?.loading, period]);

  // ── Инициализация dayIdx при загрузке дневных данных ─────────────────────
  useEffect(() => {
    if (dayIdx >= 0 || !margin?.daily?.length) return;
    const allDates = margin.daily.map(d => d.date).sort();
    const today = new Date().toISOString().slice(0, 10);
    const ti = allDates.indexOf(today);
    setDayIdx(ti >= 0 ? ti : allDates.length - 1);
  }, [margin?.daily, dayIdx]);

  // ── Обновить всё вручную ──────────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    try { await refreshOnecCache(); } catch (_) {}
    setRefreshing(false);
    loadPriority();
    loadStock();
    loadMargin(period);
    loadFinance(period);
    if (writeoffData) loadWriteoffReport(writeoffPeriod, writeoffDateFrom, writeoffDateTo);
    loadReceipts();
  };

  const debtors = debts.filter(d => d.type === 'debtor');
  const creditors = debts.filter(d => d.type === 'creditor');
  const debtorTotal = debtors.reduce((s, d) => s + d.amount, 0);
  const creditorTotal = creditors.reduce((s, d) => s + d.amount, 0);
  const visible = debtTab === 'all' ? debts : debtTab === 'debtor' ? debtors : creditors;
  const topDebtors = [...debtors].sort((a, b) => b.amount - a.amount).slice(0, 7);
  const topCreditors = [...creditors].sort((a, b) => b.amount - a.amount).slice(0, 7);
  const writeoffFiltered = (writeoffData?.items || []).filter((it) => {
    const q = writeoffSearch.toLowerCase().trim();
    return !q
      || (it.article || '').toLowerCase().includes(q)
      || (it.name || '').toLowerCase().includes(q)
      || (it.warehouse || '').toLowerCase().includes(q);
  });
  const writeoffRows = [...writeoffFiltered].sort((a, b) => {
    const dir = writeoffSort.dir === 'asc' ? 1 : -1;
    const av = a[writeoffSort.key];
    const bv = b[writeoffSort.key];
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av || '').localeCompare(String(bv || ''), 'ru') * dir;
  });

  return (
    <div>
      <div className="kp-hero">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <Plate>1С · Управление торговлей 11.5</Plate>
            <h1 style={{ margin: 0 }}>Финдиректор</h1>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
              Финансовый модуль на данных из 1С:Управление торговлей 11.5
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={handleRefresh}
              disabled={refreshing || stockLoading || marginLoading}
              title="Обновить данные из 1С (сбросить кэш)"
              style={{
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                cursor: refreshing || stockLoading || marginLoading ? 'not-allowed' : 'pointer',
                opacity: refreshing || stockLoading || marginLoading ? 0.6 : 1,
              }}
            >
              {refreshing ? '⏳ Обновление…' : '🔄 Обновить'}
            </button>
            {connected === true && (
              <span style={{
                background: 'rgba(14,159,110,0.15)', color: 'var(--green)',
                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                border: '1px solid rgba(14,159,110,0.3)',
              }}>
                ● Подключено к 1С
              </span>
            )}
            {connected === false && (
              <span style={{
                background: 'rgba(224,72,72,0.15)', color: 'var(--red)',
                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                border: '1px solid rgba(224,72,72,0.3)',
              }}>
                ● 1С не подключена
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Не подключено */}
      {connected === false && (
        <div style={{
          background: 'rgba(224,72,72,0.07)', border: '1px solid rgba(224,72,72,0.25)',
          borderRadius: 12, padding: 24, marginBottom: 24,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--red)', marginBottom: 10 }}>
            ⚠️ 1С сервер недоступен
          </div>
          <div style={{ color: 'var(--text2)', lineHeight: 1.9, fontSize: 13 }}>
            Сервер <span style={{ color: 'var(--text1)', fontWeight: 600 }}>82.196.85.118:8088</span> не отвечает.<br />
            Это нормально если компьютер с 1С выключен или вы не в офисе.<br />
            Склад и маржа загрузятся автоматически когда сервер будет доступен.
          </div>
        </div>
      )}

      <ACard id="receipts" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              background: 'var(--accent)',
              borderRadius: 8, padding: '4px 10px', fontSize: 13, color: '#fff', fontWeight: 900,
            }}>Финдиректор</span>
            <span style={{ color: 'var(--text1)' }}>Управленческий срез проекта</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>
            Период: <span style={{ color: 'var(--text1)', fontWeight: 700 }}>{period}</span>
          </div>
        </div>

        {financeData?.error ? (
          <div style={{ padding: 20, color: 'var(--red)', fontSize: 13 }}>
            ⚠️ Ошибка загрузки финансового блока: {financeData.error}
          </div>
        ) : !financeData ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            {financeLoading ? 'Загружаем финансовый срез...' : 'Финансовый срез пока недоступен.'}
          </div>
        ) : (
          <>
            {(financeLoading || financeData?.loading) && (
              <div style={{
                marginBottom: 16, padding: '10px 14px', background: 'rgba(0,167,220,0.08)',
                borderRadius: 8, border: '1px solid rgba(0,167,220,0.22)', fontSize: 12, color: 'var(--text2)',
              }}>
                Обновляем финансовые данные из 1С. Если часть цифр ещё не готова, блок покажет их автоматически после догрузки.
              </div>
            )}

            {!!financeData?.warnings?.length && (
              <div style={{
                marginBottom: 16, padding: '10px 14px', background: 'rgba(224,138,0,0.08)',
                borderRadius: 8, border: '1px solid rgba(224,138,0,0.25)', fontSize: 12, color: '#e08a00',
              }}>
                {financeData.warnings.map((w) => (
                  <div key={w} style={{ marginBottom: 4 }}>{w}</div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              <Metric
                title="Деньги в складе"
                value={fmt(financeData?.inventory_purchase)}
                sub="Текущая закупочная стоимость остатков"
                color={KA_BLUE}
                icon="🏦"
              />
              <Metric
                title="Валовая прибыль"
                value={fmtMaybeMoney(financeData?.gross_profit)}
                sub={financeData?.cost_available
                  ? `${fmtMaybePct(financeData?.gross_margin_pct)} от выручки${financeData?.cost_estimated ? ' · оценочно' : ''}`
                  : 'Себестоимость не подтверждена'}
                color={financeData?.cost_available ? '#0e9f6e' : '#e08a00'}
                icon="💸"
              />
              <Metric
                title="Опер. прибыль"
                value={fmtMaybeMoney(financeData?.operating_profit)}
                sub={financeData?.cost_available
                  ? `${fmtMaybePct(financeData?.operating_margin_pct)} после расходов${financeData?.cost_estimated ? ' · оценочно' : ''}`
                  : 'Недоступно без себестоимости'}
                color={!financeData?.cost_available ? '#e08a00' : ((financeData?.operating_profit || 0) >= 0 ? KA_GOLD2 : '#e04848')}
                icon="📊"
              />
              <Metric
                title="Жесткий неликвид"
                value={fmt(financeData?.hard_dead_stock_amount)}
                sub={
                  financeData?.hard_dead_stock_count
                    ? `${financeData.hard_dead_stock_count} SKU · ${fmtMaybePct(financeData?.hard_dead_stock_share_pct)} склада`
                    : 'Нет остатков без продаж 1 год'
                }
                color={financeData?.hard_dead_stock_count ? '#e04848' : '#0e9f6e'}
                icon="🧊"
              />
              <Metric
                title="Оборачиваемость"
                value={fmtX(financeData?.inventory_turnover_ratio)}
                sub="Выручка / деньги в складе"
                color="#00a7dc"
                icon="🔄"
              />
              <Metric
                title="Нужно на ходовой товар"
                value={fmt(financeData?.cash_needed_for_fast_items)}
                sub={`Упущенная выручка за 7д: ${fmt(financeData?.lost_revenue_week_from_understock)}`}
                color="#0e9f6e"
                icon="📦"
              />
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12, marginBottom: 16,
            }}>
              {[
                { label: 'Выручка месяца', value: fmt(financeData?.revenue), color: KA_BLUE },
                { label: 'Себестоимость', value: fmt(financeData?.cost), color: 'var(--red)' },
                { label: 'Валовая прибыль месяца', value: fmtMaybeMoney(financeData?.gross_profit), color: financeData?.cost_available ? '#0e9f6e' : '#e08a00' },
                { label: 'Зарплаты факт', value: fmt(financeData?.payroll_actual), color: '#0e9f6e' },
                { label: `Налог ${financeData?.tax_rate || 8}%`, value: fmt(financeData?.tax_amount), color: KA_GOLD2 },
                { label: 'Аренда', value: fmt(financeData?.rent_amount), color: '#e08a00' },
                { label: 'Все опер. расходы', value: fmt(financeData?.operating_expenses), color: '#e08a00' },
                { label: 'Баланс в продаже', value: fmt(financeData?.inventory_retail), color: '#00a7dc' },
                { label: 'Потенциал склада', value: fmt(financeData?.inventory_potential_margin), color: '#0e9f6e' },
                { label: 'Неликвид в продаже', value: fmt(financeData?.hard_dead_stock_retail_amount), color: financeData?.hard_dead_stock_count ? '#e04848' : '#0e9f6e' },
                { label: 'Окупаемость склада', value: fmtMaybePct(financeData?.inventory_profitability_pct), color: !financeData?.cost_available ? '#e08a00' : ((financeData?.inventory_profitability_pct || 0) >= 0 ? '#0e9f6e' : '#e04848') },
              ].map((item) => (
                <div key={item.label} style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '14px 16px',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>{item.value}</div>
                </div>
              ))}
            </div>

            <div style={{
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10,
              padding: '12px 14px', color: 'var(--text2)', fontSize: 12, lineHeight: 1.7,
            }}>
              <div>
                <span style={{ color: 'var(--text1)', fontWeight: 700 }}>Формула:</span> операционная прибыль = валовая прибыль - ФОТ - аренда - 8% налога с выручки
              </div>
              <div>
                <span style={{ color: 'var(--text1)', fontWeight: 700 }}>Смысл:</span> блок показывает, сколько проект реально зарабатывает сейчас и насколько эффективно выручка оборачивает деньги, замороженные в складе.
              </div>
            </div>

            {!!financeData?.hard_dead_stock_count && (
              <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(224,72,72,0.06)', borderRadius: 10, border: '1px solid rgba(224,72,72,0.2)' }}>
                <div style={{ fontSize: 13, color: '#e04848', fontWeight: 700 }}>
                  🧊 {financeData.hard_dead_stock_count} SKU без продаж больше 1 года — заморожено {fmt(financeData.hard_dead_stock_amount)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                  Полный список с назначением менеджеров и трекером бонусов — в блоке «Неликвид» ниже ↓
                </div>
              </div>
            )}
          </>
        )}
      </ACard>

      {/* ═══════════════════════════════════════════════════════════════════
           БЛОК: НЕЛИКВИД — КОНТРОЛЬ РЕАЛИЗАЦИИ
      ════════════════════════════════════════════════════════════════════ */}
      <ACard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              background: 'var(--red)',
              borderRadius: 8, padding: '4px 10px', fontSize: 13, color: '#fff', fontWeight: 900,
            }}>Неликвид</span>
            <span style={{ color: 'var(--text1)' }}>Контроль реализации</span>
            {deadStock && !deadStockLoading && (
              <span style={{ fontSize: 12, color: '#e04848', fontWeight: 600 }}>
                {deadStock.count} SKU · Закупка {fmt(deadStock.total_frozen)} / Реализация {fmt(deadStock.total_retail)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              placeholder="🔍 Поиск…"
              value={dsSearch}
              onChange={e => setDsSearch(e.target.value)}
              style={{
                background: 'var(--bg3)', color: 'var(--text1)',
                border: '1px solid var(--border)', padding: '7px 14px',
                borderRadius: 8, fontSize: 13, width: 180, outline: 'none',
              }}
            />
            <button onClick={loadDeadStock} style={{
              background: 'rgba(224,72,72,0.15)', color: '#e04848', border: '1px solid rgba(224,72,72,0.3)',
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}>🔄 Обновить</button>
            {deadStock?.items?.length > 0 && (
              <button onClick={() => {
                const rows = (deadStock?.items || []).filter(it => {
                  const q = dsSearch.toLowerCase();
                  return !q || (it.sku || '').toLowerCase().includes(q) || (it.name || '').toLowerCase().includes(q);
                });
                const headers = ['Зона', 'Месяцев без продаж', 'Артикул', 'Наименование', 'Остаток (шт)', 'Закупочная', 'Розничная', 'Заморожено', 'В продаже', 'Категория', 'Менеджер', 'Статус'];
                const csv = [headers, ...rows.map(it => [
                  it.bucket_label || '', it.months_without_sales || 0, it.sku, (it.name || '').replace(/,/g, ';'), it.stock,
                  it.purchase_price, it.sale_price, it.frozen_amount, it.retail_amount,
                  it.category || '', it.task?.manager || '', it.task?.status || 'не назначен',
                ])].map(r => r.join(',')).join('\n');
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url;
                a.download = `неликвид_${new Date().toISOString().slice(0,10)}.csv`;
                a.click(); URL.revokeObjectURL(url);
              }} style={{
                background: 'rgba(14,159,110,0.15)', color: 'var(--green)', border: '1px solid rgba(14,159,110,0.3)',
                padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>⬇ CSV</button>
            )}
          </div>
        </div>

        {/* Прогресс-трекер */}
        {deadStockProgress && deadStockProgress.total_assigned > 0 && (
          <div style={{
            display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap',
          }}>
            {[
              { label: 'Назначено', value: deadStockProgress.total_assigned, color: '#e08a00' },
              { label: 'Продано', value: deadStockProgress.total_sold, color: '#0e9f6e' },
              { label: 'Продано на', value: fmt(deadStockProgress.total_sold_amount), color: '#0e9f6e' },
              { label: 'Бонусы', value: fmt(deadStockProgress.total_bonus), color: KA_GOLD2 },
              { label: '% реализации', value: `${deadStockProgress.pct_sold}%`, color: deadStockProgress.pct_sold > 50 ? '#0e9f6e' : '#e08a00' },
            ].map(m => (
              <div key={m.label} style={{
                background: 'var(--bg3)', borderRadius: 8, padding: '10px 16px',
                border: '1px solid var(--border)', minWidth: 110,
              }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{m.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Рейтинг менеджеров */}
        {deadStockProgress?.by_manager?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase' }}>Менеджеры</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {deadStockProgress.by_manager.map(m => (
                <div key={m.manager} style={{
                  background: 'var(--bg3)', borderRadius: 8, padding: '8px 14px',
                  border: '1px solid var(--border)', fontSize: 12,
                }}>
                  <span style={{ fontWeight: 700, color: 'var(--text1)' }}>{m.manager}</span>
                  <span style={{ color: 'var(--text2)', marginLeft: 8 }}>
                    {m.sold}/{m.assigned} продано
                  </span>
                  {m.total_bonus > 0 && (
                    <span style={{ color: KA_GOLD2, marginLeft: 8, fontWeight: 700 }}>+{fmt(m.total_bonus)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {deadStock?.groups?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 8, fontWeight: 700, textTransform: 'uppercase' }}>
              Зоны неликвида по месяцам без продаж
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {deadStock.groups.map(g => (
                <div key={g.key} style={{
                  background: 'var(--bg3)', borderRadius: 8, padding: '8px 12px',
                  border: `1px solid ${g.color}55`, minWidth: 150,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: g.color }}>{g.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                    {g.months} · {g.count} SKU
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                    Закупка {fmt(g.total_frozen)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                    Реализация {fmt(g.total_retail)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {deadStockLoading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#e04848' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Загружаем данные по неликвиду…</div>
          </div>
        ) : !deadStock?.items?.length ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            {deadStock?.loading ? 'Данные ещё загружаются из 1С…' : 'Нет позиций без продаж 1+ месяц при наличии остатка. Отлично!'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['#', 'Зона', 'Артикул', 'Наименование', 'Месяцев без продаж', 'Остаток', 'Заморожено', 'В продаже', 'Менеджер / Действие'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text2)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(deadStock?.groups || [])
                  .map(group => ({
                    ...group,
                    items: (group.items || []).filter(it => {
                      const q = dsSearch.toLowerCase();
                      return !q || (it.sku || '').toLowerCase().includes(q) || (it.name || '').toLowerCase().includes(q);
                    }),
                  }))
                  .filter(group => group.items.length > 0)
                  .map(group => (
                    <React.Fragment key={group.key}>
                      <tr style={{ borderTop: '2px solid var(--border)', background: 'rgba(30,36,37,0.03)' }}>
                        <td colSpan={9} style={{ padding: '9px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{
                              background: `${group.color}22`, border: `1px solid ${group.color}55`, color: group.color,
                              fontWeight: 800, fontSize: 11, padding: '3px 9px', borderRadius: 999,
                            }}>
                              {group.label}
                            </span>
                            <span style={{ color: 'var(--text2)', fontSize: 12 }}>
                              {group.months} без продаж · {group.count} SKU · {fmt(group.total_frozen)} заморожено
                            </span>
                          </div>
                        </td>
                      </tr>
                      {group.items.map((it, i) => (
                        <tr key={it.sku + group.key + i} style={{ borderBottom: '1px solid var(--border)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '7px 10px', color: 'var(--text2)', fontSize: 10 }}>{i + 1}</td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                            <span style={{ background: `${it.bucket_color}22`, border: `1px solid ${it.bucket_color}55`, color: it.bucket_color, fontWeight: 700, fontSize: 10, padding: '2px 7px', borderRadius: 999 }}>
                              {it.bucket_months}
                            </span>
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <span style={{ background: 'rgba(224,72,72,0.15)', border: '1px solid rgba(224,72,72,0.4)', color: '#e04848', fontWeight: 800, fontSize: 11, padding: '2px 7px', borderRadius: 4, fontFamily: 'monospace' }}>{it.sku || '—'}</span>
                          </td>
                          <td style={{ padding: '7px 10px', fontWeight: 500, maxWidth: 250 }}>{it.name}</td>
                          <td style={{ padding: '7px 10px', color: 'var(--text2)' }}>{it.months_without_sales} мес</td>
                          <td style={{ padding: '7px 10px', fontWeight: 700, color: '#e04848' }}>{fmtN(it.stock)} шт</td>
                          <td style={{ padding: '7px 10px', fontWeight: 800, color: '#e04848' }}>{fmt(it.frozen_amount)}</td>
                          <td style={{ padding: '7px 10px', color: '#00a7dc' }}>{fmt(it.retail_amount)}</td>
                          <td style={{ padding: '7px 10px', minWidth: 220 }}>
                            {it.task ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{
                                  background: it.task.status === 'sold' ? 'rgba(14,159,110,0.15)' : 'rgba(224,138,0,0.15)',
                                  color: it.task.status === 'sold' ? '#0e9f6e' : '#e08a00',
                                  padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                                }}>
                                  {it.task.status === 'assigned' ? '📋 ' : it.task.status === 'partial' ? '⏳ ' : '✅ '}
                                  {it.task.manager}
                                </span>
                                {it.task.sold_qty > 0 && (
                                  <span style={{ fontSize: 10, color: '#0e9f6e' }}>
                                    {fmtN(it.task.sold_qty)} шт / {fmt(it.task.sold_amount)}
                                  </span>
                                )}
                                {(it.task.status === 'assigned' || it.task.status === 'partial') && (
                                  <button onClick={() => { setDsSelling(it.task.task_id); setDsSoldQty(''); setDsSoldAmount(''); }}
                                    style={{ background: 'rgba(14,159,110,0.15)', color: '#0e9f6e', border: '1px solid rgba(14,159,110,0.3)', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                                    💰 Продано
                                  </button>
                                )}
                              </div>
                            ) : dsAssigning === it.sku ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input placeholder="Менеджер" value={dsManager} onChange={e => setDsManager(e.target.value)}
                                  style={{ background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', padding: '4px 8px', borderRadius: 4, fontSize: 11, width: 100 }} />
                                <input placeholder="%" value={dsBonusPct} onChange={e => setDsBonusPct(e.target.value)} type="number"
                                  style={{ background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', padding: '4px 8px', borderRadius: 4, fontSize: 11, width: 40 }} />
                                <button onClick={() => {
                                  if (!dsManager.trim()) return;
                                  assignDeadStock({
                                    sku: it.sku, nom_key: it.nom_key, name: it.name,
                                    stock_qty: it.stock, purchase_price: it.purchase_price,
                                    sale_price: it.sale_price, frozen_amount: it.frozen_amount,
                                    retail_amount: it.retail_amount, manager: dsManager.trim(),
                                    bonus_pct: parseFloat(dsBonusPct) || 5,
                                  }).then(() => { setDsAssigning(null); setDsManager(''); loadDeadStock(); })
                                    .catch(e => alert(e.response?.data?.detail || 'Ошибка'));
                                }} style={{ background: '#0e9f6e', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✓</button>
                                <button onClick={() => setDsAssigning(null)}
                                  style={{ background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>✕</button>
                              </div>
                            ) : (
                              <button onClick={() => { setDsAssigning(it.sku); setDsManager(''); setDsBonusPct(5); }}
                                style={{ background: 'rgba(224,138,0,0.15)', color: '#e08a00', border: '1px solid rgba(224,138,0,0.3)', padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                👤 Назначить
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Модалка продажи */}
        {dsSelling && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }} onClick={() => setDsSelling(null)}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'var(--bg2)', borderRadius: 16, padding: 28, minWidth: 320,
              border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 16, color: '#0e9f6e' }}>💰 Фиксация продажи</div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>Количество продано (шт)</div>
                <input value={dsSoldQty} onChange={e => setDsSoldQty(e.target.value)} type="number"
                  style={{ width: '100%', background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', padding: '10px 14px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>Сумма продажи (₽)</div>
                <input value={dsSoldAmount} onChange={e => setDsSoldAmount(e.target.value)} type="number"
                  style={{ width: '100%', background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', padding: '10px 14px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => {
                  const qty = parseFloat(dsSoldQty);
                  const amount = parseFloat(dsSoldAmount);
                  if (!qty || !amount) return;
                  markDeadStockSold(dsSelling, { sold_qty: qty, sold_amount: amount })
                    .then(() => { setDsSelling(null); loadDeadStock(); })
                    .catch(e => alert(e.response?.data?.detail || 'Ошибка'));
                }} style={{
                  flex: 1, background: '#0e9f6e', color: '#fff', border: 'none',
                  padding: '12px', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer',
                }}>✓ Записать продажу</button>
                <button onClick={() => setDsSelling(null)} style={{
                  background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)',
                  padding: '12px 20px', borderRadius: 10, fontSize: 14, cursor: 'pointer',
                }}>Отмена</button>
              </div>
            </div>
          </div>
        )}
      </ACard>

      {/* KPI — ПРОДАЖИ / МАРЖА / ФОТ */}
      {(() => {
        // ФОТ константы
        const KLADOVSHIK    = 130000;
        const SBORSHIK      = 70000;
        const MGR_BASE      = 30000;
        const MGR_COUNT     = 2;
        const MGR_BONUS_PCT = 0.10;

        // Навигация по дням (shared state: dayIdx, dayFilter)
        const dailyArr    = (margin?.daily || []).filter(d => d.date);
        const todayStr    = new Date().toISOString().slice(0, 10);
        const allDates    = dailyArr.map(d => d.date).sort();
        const filteredDates = dayFilter ? allDates.filter(d => d.startsWith(dayFilter)) : allDates;
        const effectiveIdx  = dayIdx < 0
          ? (() => { const ti = allDates.indexOf(todayStr); return ti >= 0 ? ti : allDates.length - 1; })()
          : dayIdx;
        const safeIdx  = filteredDates.length > 0 ? Math.max(0, Math.min(effectiveIdx, filteredDates.length - 1)) : 0;
        const selDate  = filteredDates[safeIdx] || todayStr;
        const isToday  = selDate === todayStr;
        const months   = [...new Set(allDates.map(d => d.slice(0, 7)))].sort();
        const dayLabel = selDate
          ? new Date(selDate + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' })
          : '';

        // Режим «показывать месяц целиком» — включается когда выбран конкретный месяц
        const showMonthMode = !!dayFilter;

        // Данные выбранного дня:
        const pLoading  = priorityLoading || !priorityData || priorityData.loading;
        const selDayRaw = dailyArr.find(d => d.date === selDate) || { revenue: 0, cost: 0, margin: 0, qty: 0 };

        // Агрегат за выбранный месяц из dailyArr (если выбран месяц)
        const filteredMonthDays = dayFilter ? dailyArr.filter(d => d.date.startsWith(dayFilter)) : [];
        const monthAgg = filteredMonthDays.reduce((acc, d) => ({
          revenue: acc.revenue + (d.revenue || 0),
          cost:    acc.cost    + (d.cost    || 0),
          margin:  acc.margin  + (d.margin  || 0),
          qty:     acc.qty     + (d.qty     || 0),
        }), { revenue: 0, cost: 0, margin: 0, qty: 0 });

        const selRev    = showMonthMode ? monthAgg.revenue
          : (isToday && !pLoading ? (priorityData?.today?.revenue ?? selDayRaw.revenue) : selDayRaw.revenue);
        const selQty    = showMonthMode ? monthAgg.qty
          : (isToday && !pLoading ? (priorityData?.today?.qty     ?? selDayRaw.qty)     : selDayRaw.qty);
        const selCost   = showMonthMode ? monthAgg.cost
          : (isToday && !pLoading ? (priorityData?.today?.cost    ?? selDayRaw.cost)    : selDayRaw.cost);
        const selMargin = showMonthMode ? monthAgg.margin
          : (isToday && !pLoading ? (priorityData?.today?.margin  ?? selDayRaw.margin)  : selDayRaw.margin);

        const monthRev    = showMonthMode ? monthAgg.revenue : (priorityData?.month?.revenue ?? (margin?.current?.revenue ?? 0));
        const monthCostRaw = showMonthMode ? monthAgg.cost : (priorityData?.month?.cost ?? (margin?.current?.cost ?? 0));
        const anyMonthlyCost = margin?.monthly?.some(m => m.cost > 0) ?? false;
        const costAvailable  = selCost > 0 || monthCostRaw > 0 || anyMonthlyCost;
        const monthMargin    = showMonthMode ? monthAgg.margin : (priorityData?.month?.margin ?? (margin?.current?.margin ?? 0));

        const fotFixed = KLADOVSHIK + SBORSHIK + MGR_BASE * MGR_COUNT;
        const fotBonus = monthMargin ? Math.round(monthMargin * MGR_BONUS_PCT) : 0;
        const fotTotal = fotFixed + fotBonus;
        const fotPct   = monthMargin > 0 ? Math.round(fotTotal / monthMargin * 100) : 0;

        // Показываем спиннер только для сегодняшнего дня если priority ещё грузится
        const dataLoading = isToday ? pLoading : !dailyArr.length;

        return (
          <div style={{ marginBottom: 24 }}>

            {/* Панель навигации по дням (появляется как только загрузится history) */}
            {dailyArr.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 12, flexWrap: 'wrap', gap: 8,
              }}>
                <select
                  value={dayFilter}
                  onChange={e => {
                    const newFilter = e.target.value;
                    setDayFilter(newFilter);
                    // При выборе месяца — прыгаем на последний день этого месяца
                    if (newFilter) {
                      const datesInMonth = allDates.filter(d => d.startsWith(newFilter));
                      setDayIdx(Math.max(0, datesInMonth.length - 1));
                    } else {
                      setDayIdx(-1); // сброс → авто-сегодня
                    }
                  }}
                  style={{
                    background: dayFilter ? 'rgba(224,138,0,0.08)' : 'var(--bg1)',
                    color: 'var(--text1)',
                    border: `1px solid ${dayFilter ? 'rgba(224,138,0,0.4)' : 'var(--border)'}`,
                    borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                    fontWeight: dayFilter ? 700 : 400,
                  }}
                >
                  <option value="">Все месяцы</option>
                  {months.map(m => (
                    <option key={m} value={m}>
                      {new Date(m + '-15').toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
                    </option>
                  ))}
                </select>
                {dayFilter && (
                  <span style={{
                    fontSize: 11, color: KA_GOLD, fontWeight: 600,
                    background: 'rgba(224,138,0,0.1)', padding: '3px 8px', borderRadius: 5,
                    border: '1px solid rgba(224,138,0,0.3)',
                  }}>
                    📅 Итог за месяц
                  </span>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={() => setDayIdx(Math.max(0, safeIdx - 1))}
                    disabled={safeIdx <= 0}
                    style={{
                      background: safeIdx > 0 ? 'rgba(0,167,220,0.15)' : 'var(--bg1)',
                      color: safeIdx > 0 ? KA_BLUE : 'var(--text2)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      width: 32, height: 32, fontSize: 16,
                      cursor: safeIdx > 0 ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >◀</button>
                  <div style={{ textAlign: 'center', minWidth: 160 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text1)' }}>{dayLabel}</div>
                    {isToday && <div style={{ fontSize: 10, color: '#0e9f6e', fontWeight: 600 }}>● Сегодня (live)</div>}
                  </div>
                  <button
                    onClick={() => setDayIdx(Math.min(filteredDates.length - 1, safeIdx + 1))}
                    disabled={safeIdx >= filteredDates.length - 1}
                    style={{
                      background: safeIdx < filteredDates.length - 1 ? 'rgba(0,167,220,0.15)' : 'var(--bg1)',
                      color: safeIdx < filteredDates.length - 1 ? KA_BLUE : 'var(--text2)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      width: 32, height: 32, fontSize: 16,
                      cursor: safeIdx < filteredDates.length - 1 ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >▶</button>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>

              {/* Карточка 1 — Отгружено */}
              <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '18px 20px' }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🛒</span> {showMonthMode
                    ? `Отгружено — ${new Date(dayFilter + '-15').toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`
                    : `Отгружено ${isToday ? 'сегодня' : dayLabel}`}
                </div>
                {dataLoading ? (
                  <div style={{ fontSize: 14, color: 'var(--text2)' }}>⏳ загружаем…</div>
                ) : (
                  <>
                    <div style={{ fontSize: 26, fontWeight: 800, color: KA_BLUE }}>{fmt(selRev)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                      {Math.round(selQty)} шт
                      {!showMonthMode && <> · месяц: {fmt(monthRev)}</>}
                      {showMonthMode && filteredMonthDays.length > 0 && <> · {filteredMonthDays.length} рабочих дней</>}
                    </div>
                  </>
                )}
              </div>

              {/* Карточка 2 — Маржа */}
              <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '18px 20px' }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>📈</span> {showMonthMode
                    ? `Маржа — ${new Date(dayFilter + '-15').toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`
                    : `Маржа ${isToday ? 'сегодня' : dayLabel}`}
                </div>
                {dataLoading ? (
                  <div style={{ fontSize: 14, color: 'var(--text2)' }}>⏳ загружаем…</div>
                ) : !costAvailable ? (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#e08a00' }}>⚠️ н/д</div>
                    <div style={{ fontSize: 11, color: '#e08a00', marginTop: 4, lineHeight: 1.5 }}>
                      Себестоимость не рассчитана.<br/>
                      <span style={{ color: 'var(--text2)' }}>Запустите «Закрытие месяца →<br/>Расчёт себестоимости» в 1С</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#0e9f6e' }}>{fmt(selMargin)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                      {selRev > 0 && selMargin > 0 ? `${Math.round(selMargin / selRev * 100)}% маржинальность` : ''}
                      {!showMonthMode && <> · месяц: {fmt(monthMargin)}</>}
                    </div>
                  </>
                )}
              </div>

              {/* Карточка 3 — ФОТ */}
              <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', padding: '18px 20px' }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>💼</span> ФОТ — {period}
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, color: KA_GOLD }}>{fmt(fotTotal)}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6, lineHeight: 1.6 }}>
                  <div>🏗️ Кладовщик: {fmt(KLADOVSHIK)}</div>
                  <div>🔧 Сборщик: {fmt(SBORSHIK)}</div>
                  <div>👔 Менеджеры ({MGR_COUNT}×): {fmt(MGR_BASE * MGR_COUNT)} оклад
                    {costAvailable && fotBonus > 0 && <span style={{ color: '#0e9f6e' }}> + {fmt(fotBonus)} бонус (10% маржи)</span>}
                    {!costAvailable && <span style={{ color: '#e08a00' }}> + бонус н/д</span>}
                  </div>
                  {costAvailable && monthMargin > 0 && (
                    <div style={{ marginTop: 2, color: fotPct > 40 ? '#e04848' : 'var(--text2)' }}>
                      ФОТ/маржа: {fotPct}%
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════
           БЛОК: ЕЖЕДНЕВНАЯ НАВИГАЦИЯ — Продажи и Маржа
      ════════════════════════════════════════════════════════════════════ */}
      {(() => {
        const dailyArr = (margin?.daily || []).filter(d => d.date);
        if (!dailyArr.length) return null;
        const today = new Date().toISOString().slice(0, 10);
        const allDates = dailyArr.map(d => d.date).sort();
        const filtered = dayFilter ? allDates.filter(d => d.startsWith(dayFilter)) : allDates;
        const effectiveIdx = dayIdx < 0 ? (() => { const ti = allDates.indexOf(today); return ti >= 0 ? ti : allDates.length - 1; })() : dayIdx;
        if (!filtered.length) return null;
        const safeIdx = Math.max(0, Math.min(effectiveIdx, filtered.length - 1));
        const selDate = filtered[safeIdx] || '';
        const dayData = dailyArr.find(d => d.date === selDate) || { revenue: 0, cost: 0, margin: 0, qty: 0 };
        const marginPct = dayData.revenue > 0 ? Math.round(dayData.margin / dayData.revenue * 100) : 0;
        const months = [...new Set(allDates.map(d => d.slice(0, 7)))].sort();
        const dayLabel = selDate ? new Date(selDate + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' }) : '';
        const isToday = selDate === today;

        return (
          <ACard style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ background: 'var(--accent)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>📅</span>
                <span>Продажи и маржа по дням</span>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <select
                  value={dayFilter}
                  onChange={e => { setDayFilter(e.target.value); setDayIdx(0); }}
                  style={{
                    background: 'var(--bg1)', color: 'var(--text1)', border: '1px solid var(--border)',
                    borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  <option value="">Все дни (30)</option>
                  {months.map(m => <option key={m} value={m}>{new Date(m + '-15').toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16 }}>
              <button
                onClick={() => setDayIdx(Math.max(0, safeIdx - 1))}
                disabled={safeIdx <= 0}
                style={{
                  background: safeIdx > 0 ? 'rgba(0,167,220,0.15)' : 'var(--bg1)',
                  color: safeIdx > 0 ? KA_BLUE : 'var(--text2)',
                  border: '1px solid var(--border)', borderRadius: 10,
                  width: 40, height: 40, fontSize: 18, cursor: safeIdx > 0 ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >◀</button>
              <div style={{ textAlign: 'center', minWidth: 180 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text1)' }}>{dayLabel}</div>
                <div style={{ fontSize: 11, color: isToday ? '#0e9f6e' : 'var(--text2)', fontWeight: 600 }}>
                  {isToday ? '● Сегодня' : selDate}
                </div>
              </div>
              <button
                onClick={() => setDayIdx(Math.min(filtered.length - 1, safeIdx + 1))}
                disabled={safeIdx >= filtered.length - 1}
                style={{
                  background: safeIdx < filtered.length - 1 ? 'rgba(0,167,220,0.15)' : 'var(--bg1)',
                  color: safeIdx < filtered.length - 1 ? KA_BLUE : 'var(--text2)',
                  border: '1px solid var(--border)', borderRadius: 10,
                  width: 40, height: 40, fontSize: 18, cursor: safeIdx < filtered.length - 1 ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >▶</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
              <div style={{ background: 'rgba(0,167,220,0.08)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 4 }}>Продажи</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: KA_BLUE }}>{fmt(dayData.revenue)}</div>
              </div>
              <div style={{ background: 'rgba(14,159,110,0.08)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 4 }}>Маржа</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#0e9f6e' }}>{fmt(dayData.margin)}</div>
              </div>
              <div style={{ background: 'rgba(224,138,0,0.08)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 4 }}>Маржин-ть</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#e08a00' }}>{marginPct}%</div>
              </div>
              <div style={{ background: 'rgba(0,167,220,0.08)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 4 }}>Кол-во</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#00a7dc' }}>{Math.round(dayData.qty)} шт</div>
              </div>
            </div>
          </ACard>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════
           БЛОК: ОСТАТКИ ТОВАРОВ
      ════════════════════════════════════════════════════════════════════ */}
      <ACard style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              background: 'var(--accent)',
              borderRadius: 8, padding: '4px 10px', fontSize: 13, color: '#fff', fontWeight: 900,
            }}>Склад</span>
            <span style={{ color: 'var(--text1)' }}>Остатки товаров</span>
            {stockData && !stockLoading && (
              <span style={{ fontSize: 12, color: KA_BLUE, fontWeight: 600 }}>
                {stockData.total_positions} позиций
              </span>
            )}
            {stockData?.prices_loading && !stockLoading && (
              <span style={{ fontSize: 11, color: '#e08a00', fontWeight: 500 }}>· цены загружаются…</span>
            )}
          </div>
          <input
            placeholder="🔍 Поиск товара…"
            value={stockSearch}
            onChange={e => setStockSearch(e.target.value)}
            style={{
              background: 'var(--bg3)', color: 'var(--text1)',
              border: '1px solid var(--border)', padding: '7px 14px',
              borderRadius: 8, fontSize: 13, width: 220, outline: 'none',
            }}
          />
        </div>

        {stockLoading ? (
          <div style={{ padding: 32, textAlign: 'center', color: KA_GOLD }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: KA_GOLD, marginBottom: 6 }}>Загружаем остатки из 1С…</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>Первый запрос занимает 1–2 минуты, далее данные кэшируются на 30 мин.</div>
          </div>
        ) : stockData?.error ? (
          <div style={{ padding: 20, color: 'var(--red)', fontSize: 13 }}>
            ⚠️ Ошибка получения остатков: {stockData.error}
          </div>
        ) : (
          <>
            {/* Топ-10 баром */}
            {stockData?.items?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Топ-10 по остатку
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={stockData.items.slice(0, 10)} layout="vertical" margin={{ left: 0, right: 20 }}>
                    <XAxis type="number" tickFormatter={v => fmtN(v)} tick={{ fill: 'var(--text2)', fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" width={160}
                      tick={{ fill: 'var(--text2)', fontSize: 10 }}
                      tickFormatter={v => v.length > 20 ? v.slice(0, 20) + '…' : v} />
                    <Tooltip formatter={(v) => [fmtN(v) + ' шт', 'Остаток']}
                      contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="qty" radius={[0, 4, 4, 0]}>
                      {stockData.items.slice(0, 10).map((_, i) => (
                        <Cell key={i} fill={KA_GOLD2} fillOpacity={0.9 - i * 0.07} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Таблица остатков с ценами и сортировкой */}
            {(() => {
              const COLS = [
                { key: 'idx',               label: '#',               align: 'left',  width: 36,  sortable: false },
                { key: 'article',           label: 'Артикул',         align: 'left',  width: 90,  sortable: true  },
                { key: 'name',              label: 'Наименование',    align: 'left',              sortable: true  },
                { key: 'qty',               label: 'Остаток',         align: 'right',             sortable: true  },
                { key: 'cost_unit',         label: 'С/с за шт',       align: 'right',             sortable: true  },
                { key: 'total_cost',        label: 'С/с итого',       align: 'right',             sortable: true  },
                { key: 'price_unit',        label: 'Цена',            align: 'right',             sortable: true  },
                { key: 'potential_revenue', label: 'Потенц. выручка', align: 'right',             sortable: true  },
                { key: 'potential_margin',  label: 'Потенц. маржа',   align: 'right',             sortable: true  },
              ];

              const toggleSort = (key) => {
                setStockSort(prev =>
                  prev.key === key
                    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                    : { key, dir: (key === 'name' || key === 'article') ? 'asc' : 'desc' }
                );
              };

              const filtered = (stockData?.items || [])
                .filter(it => !stockSearch || it.name.toLowerCase().includes(stockSearch.toLowerCase())
                  || (it.article || '').toLowerCase().includes(stockSearch.toLowerCase()));

              const sorted = [...filtered].sort((a, b) => {
                const { key, dir } = stockSort;
                const av = (key === 'name' || key === 'article') ? (a[key] || '') : (a[key] ?? 0);
                const bv = (key === 'name' || key === 'article') ? (b[key] || '') : (b[key] ?? 0);
                const cmp = typeof av === 'string' ? av.localeCompare(bv, 'ru') : av - bv;
                return dir === 'asc' ? cmp : -cmp;
              });

              const arrow = (key) => {
                if (stockSort.key !== key) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
                return <span style={{ marginLeft: 3, color: KA_BLUE }}>{stockSort.dir === 'asc' ? '↑' : '↓'}</span>;
              };

              return (
                <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {COLS.map(h => (
                          <th key={h.key}
                            onClick={() => h.sortable && toggleSort(h.key)}
                            style={{
                              padding: '8px 10px', textAlign: h.align,
                              color: stockSort.key === h.key ? KA_BLUE : 'var(--text2)',
                              fontSize: 10, fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.05em',
                              whiteSpace: 'nowrap', width: h.width,
                              cursor: h.sortable ? 'pointer' : 'default',
                              userSelect: 'none',
                            }}
                          >
                            {h.label}{h.sortable && arrow(h.key)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((it, i) => {
                        const hasPrices = it.cost_unit > 0 || it.price_unit > 0;
                        const marginPct = it.price_unit > 0 && it.cost_unit > 0
                          ? Math.round((it.price_unit - it.cost_unit) / it.price_unit * 100) : null;
                        return (
                          <tr key={it.nom_key || i} style={{ borderBottom: '1px solid var(--border)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <td style={{ padding: '7px 10px', color: 'var(--text2)', fontSize: 10 }}>{i + 1}</td>
                            <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                              {it.article
                                ? <span style={{
                                    background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)',
                                    color: KA_GOLD, fontWeight: 800, fontSize: 11,
                                    padding: '2px 7px', borderRadius: 4, letterSpacing: '0.05em',
                                    fontFamily: 'monospace',
                                  }}>{it.article}</span>
                                : <span style={{ color: 'var(--text2)', fontSize: 10 }}>—</span>
                              }
                            </td>
                            <td style={{ padding: '7px 10px', fontWeight: 500, maxWidth: 280 }}>
                              <span style={{ verticalAlign: 'middle' }}>{it.name}</span>
                              {it.qty === 0 && <span style={{ marginLeft: 6, fontSize: 9, color: '#e04848', fontWeight: 700 }}>⛔ НЕТ</span>}
                              {it.qty > 0 && it.qty < 5 && <span style={{ marginLeft: 6, fontSize: 9, color: '#e08a00', fontWeight: 700 }}>мало</span>}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap',
                              color: it.qty === 0 ? '#e04848' : it.qty < 5 ? '#e08a00' : it.qty < 20 ? '#e08a00' : KA_GOLD }}>
                              {it.qty === 0
                                ? <span style={{ background: 'rgba(224,72,72,0.12)', padding: '2px 7px', borderRadius: 4 }}>НЕТ</span>
                                : `${fmtN(it.qty)} шт`}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', color: hasPrices ? 'var(--text1)' : 'var(--text2)', whiteSpace: 'nowrap' }}>
                              {hasPrices ? fmt(it.cost_unit) : '—'}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', color: hasPrices ? '#e04848' : 'var(--text2)', whiteSpace: 'nowrap' }}>
                              {hasPrices ? fmt(it.total_cost) : '—'}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', color: hasPrices ? 'var(--text1)' : 'var(--text2)', whiteSpace: 'nowrap' }}>
                              {hasPrices ? fmt(it.price_unit) : '—'}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', color: hasPrices ? KA_BLUE : 'var(--text2)', whiteSpace: 'nowrap' }}>
                              {hasPrices ? fmt(it.potential_revenue) : '—'}
                            </td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {hasPrices ? (
                                <span style={{ color: it.potential_margin > 0 ? '#0e9f6e' : '#e04848', fontWeight: 700 }}>
                                  {fmt(it.potential_margin)}
                                  {marginPct !== null && <span style={{ fontSize: 10, marginLeft: 4, color: 'var(--text2)' }}>{marginPct}%</span>}
                                </span>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {!stockData?.items?.length && (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--text2)' }}>
                      Нет данных об остатках — ожидаем подключения 1С
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </ACard>

      {/* ═══════════════════════════════════════════════════════════════════
           БЛОК: БАЛАНС СКЛАДА
      ════════════════════════════════════════════════════════════════════ */}
      <ACard style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{
            background: 'var(--accent)',
            borderRadius: 8, padding: '4px 10px', fontSize: 13, color: '#fff', fontWeight: 900,
          }}>Баланс склада</span>
        </div>

        {stockLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text2)' }}>Загружаем цены из 1С…</div>
          </div>
        ) : stockData?.prices_loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: '#e08a00' }}>⏳ Цены ещё не готовы (5–10 мин при старте сервера)</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>Остатки уже доступны в таблице выше</div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Metric
              title="Баланс в закупке"
              value={fmt(stockData?.totals?.total_purchase)}
              sub="Остаток × себестоимость"
              color="#0e9f6e"
              icon="🏷️"
            />
            <Metric
              title="Баланс в продаже"
              value={fmt(stockData?.totals?.total_retail)}
              sub="Остаток × цена реализации"
              color={KA_BLUE}
              icon="🛒"
            />
            <Metric
              title="Потенциальная маржа"
              value={fmt(stockData?.totals?.potential_margin)}
              sub={
                stockData?.totals?.total_purchase > 0
                  ? `${Math.round(((stockData.totals.potential_margin || 0) / stockData.totals.total_purchase) * 100)}% наценка`
                  : ''
              }
              color={KA_GOLD2}
              icon="📈"
            />
          </div>
        )}
      </ACard>

      {/* ═══════════════════════════════════════════════════════════════════
           БЛОК: МАРЖА ПРОДАННЫХ ТОВАРОВ
      ════════════════════════════════════════════════════════════════════ */}
      <ACard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              background: 'var(--accent)',
              borderRadius: 8, padding: '4px 10px', fontSize: 13, color: '#fff', fontWeight: 900,
            }}>Маржа</span>
            <span style={{ color: 'var(--text1)' }}>Ежемесячный счётчик</span>
          </div>
          <input
            type="month" value={period}
            onChange={e => setPeriod(e.target.value)}
            style={{
              background: 'var(--bg3)', color: 'var(--text1)',
              border: '1px solid var(--border)', padding: '7px 12px',
              borderRadius: 7, fontSize: 13,
            }}
          />
        </div>

        {margin?.error ? (
          <div style={{ padding: 20, color: 'var(--red)', fontSize: 13 }}>
            ⚠️ Ошибка: {margin.error}
          </div>
        ) : (
          <>
            {/* Уведомление пока полная история ещё грузится (не блокирует цифры) */}
            {(marginLoading || margin?.loading) && (
              <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(0,167,220,0.08)', borderRadius: 8, border: '1px solid rgba(0,167,220,0.2)', fontSize: 12, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>⏳</span>
                <span>
                  {margin?.loading
                    ? 'Полная история маржи загружается на сервере 1С (5–10 мин). График и таблица товаров появятся позже.'
                    : 'Загружаем данные из 1С… Ключевые цифры уже показаны ниже.'}
                </span>
              </div>
            )}

            {/* KPI текущего месяца — используем margin.current или priorityData.month как фолбек */}
            {(() => {
              const cur = (margin?.current && Object.keys(margin.current).length > 0)
                ? margin.current
                : (!priorityLoading && priorityData?.month?.revenue > 0 ? {
                    revenue: priorityData.month.revenue,
                    cost: priorityData.month.cost,
                    margin: priorityData.month.margin,
                    margin_pct: priorityData.month.margin_pct,
                    qty: priorityData.month.qty,
                  } : null);
              if (!cur) return (
                <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
                  Данные за {period} загружаются…
                </div>
              );
              return (
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Выручка', value: fmt(cur.revenue), color: KA_BLUE, icon: '💰' },
                    { label: 'Себестоимость', value: cur.cost > 0 ? fmt(cur.cost) : '⚠️ н/д', color: cur.cost > 0 ? 'var(--red)' : '#e08a00', icon: '📦' },
                    { label: 'Маржа', value: cur.cost > 0 ? fmt(cur.margin) : '⚠️ н/д', color: cur.cost > 0 ? 'var(--green)' : '#e08a00', icon: '✅' },
                    { label: 'Маржинальность', value: cur.cost > 0 ? `${cur.margin_pct ?? 0}%` : '⚠️ н/д', color: cur.cost > 0 ? KA_GOLD : '#e08a00', icon: '📈' },
                    { label: 'Кол-во продаж', value: fmtN(cur.qty) + ' шт', color: 'var(--text1)', icon: '🛒' },
                  ].map((m, i) => (
                    <div key={i} style={{
                      flex: '1 1 130px', background: 'var(--bg3)',
                      borderRadius: 14, padding: '14px 16px',
                    }}>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {m.icon} {m.label}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: m.color }}>{m.value}</div>
                      <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 2 }}>{period}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* График маржи по месяцам */}
            {margin?.monthly?.length > 0 ? (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Динамика маржи по месяцам
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={margin.monthly} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="period" tick={{ fill: 'var(--text2)', fontSize: 10 }} />
                    <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}к`} tick={{ fill: 'var(--text2)', fontSize: 10 }} width={50} />
                    <Tooltip
                      formatter={(v, name) => [fmt(v), name]}
                      contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    />
                    <Line type="monotone" dataKey="revenue" stroke={KA_BLUE} strokeWidth={2} dot={false} name="Выручка" />
                    <Line type="monotone" dataKey="cost" stroke="var(--red)" strokeWidth={2} dot={false} name="Себестоимость" />
                    <Line type="monotone" dataKey="margin" stroke="var(--green)" strokeWidth={2.5} dot={{ fill: 'var(--green)', r: 4 }} name="Маржа" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ padding: 20, color: 'var(--text2)', textAlign: 'center', fontSize: 13 }}>
                Нет данных о продажах — ожидаем подключения 1С
              </div>
            )}

            {/* Топ товаров по марже */}
            {margin?.top_items?.length > 0 && (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Топ-20 товаров по марже
                </div>
                <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1 }}>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['#', 'Товар', 'Выручка', 'Себест.', 'Маржа', '%', 'Кол-во'].map(h => (
                          <th key={h} style={{ padding: '7px 10px', textAlign: h === '#' || h === 'Товар' ? 'left' : 'right', color: 'var(--text2)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {margin.top_items.map((it, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '7px 10px', color: 'var(--text2)', width: 30 }}>{i + 1}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 500, maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', color: KA_BLUE }}>{fmt(it.revenue)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text2)' }}>{fmt(it.cost)}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: it.cost > 0 ? (it.margin >= 0 ? 'var(--green)' : 'var(--red)') : '#e08a00' }}>
                            {it.cost > 0 ? fmt(it.margin) : '⚠️ н/д'}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                            {it.cost > 0 ? (
                              <span style={{
                                background: it.margin_pct >= 50 ? 'rgba(14,159,110,0.15)' : it.margin_pct >= 20 ? 'rgba(224,138,0,0.15)' : 'rgba(224,72,72,0.15)',
                                color: it.margin_pct >= 50 ? 'var(--green)' : it.margin_pct >= 20 ? KA_GOLD : 'var(--red)',
                                padding: '2px 6px', borderRadius: 4, fontWeight: 700, fontSize: 11,
                              }}>{it.margin_pct}%</span>
                            ) : (
                              <span style={{ color: '#e08a00', fontSize: 11 }}>н/д</span>
                            )}
                          </td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--text2)' }}>{fmtN(it.qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </ACard>


      {/* ── Отчёт о продажах ── */}
      <ACard style={{ marginBottom: 24 }}>
        {/* Заголовок */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 20 }}>📦</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Отчёт о продажах</span>
          {salesData?.label && (
            <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>
              — {salesData.label}
            </span>
          )}
          {/* Экспорт CSV */}
          {salesData?.items?.length > 0 && (
            <button onClick={() => {
              const rows = salesData.items.filter(it => {
                const q = salesSearch.toLowerCase();
                return !q || (it.article || '').toLowerCase().includes(q) || (it.name || '').toLowerCase().includes(q) || (it.customer || '').toLowerCase().includes(q);
              });
              const headers = ['Артикул', 'Наименование', 'Кол-во (шт)', 'С/с итого', 'С/с (ед)', 'Цена продажи (ед)', 'Выручка итого', 'Маржа', 'Маржа %', 'Кому реализовано'];
              const csv = [headers, ...rows.map(it => [
                it.article || '',
                (it.name || '').replace(/,/g, ';'),
                it.qty,
                it.cost.toFixed(2),
                it.cost_per_unit ? it.cost_per_unit.toFixed(2) : '0.00',
                it.price_per_unit.toFixed(2),
                it.revenue.toFixed(2),
                (it.margin ?? (it.revenue - it.cost)).toFixed(2),
                (it.margin_pct ?? 0).toFixed(1) + '%',
                (it.customer || '').replace(/,/g, ';'),
              ])].map(r => r.join(',')).join('\n');
              const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url;
              a.download = `продажи_${salesData.date_from}_${salesData.date_to}.csv`;
              a.click(); URL.revokeObjectURL(url);
            }} style={{
              marginLeft: 'auto',
              background: 'rgba(14,159,110,0.15)', border: '1px solid rgba(14,159,110,0.3)',
              color: 'var(--green)', padding: '6px 14px', borderRadius: 8,
              fontWeight: 700, fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              ⬇ Скачать CSV
            </button>
          )}
        </div>

        {/* Фильтры — период + произвольный диапазон + Готово */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
          background: 'var(--bg3)', borderRadius: 10, padding: '10px 14px', flexWrap: 'wrap',
        }}>
          {/* Быстрые периоды */}
          <div style={{ display: 'flex', background: 'var(--bg2)', borderRadius: 7, padding: 2 }}>
            {[{ id: 'day', label: 'День' }, { id: 'week', label: 'Неделя' }, { id: 'month', label: 'Месяц' }].map(p => (
              <button key={p.id}
                onClick={() => { setSalesPeriod(p.id); setSalesDateFrom(''); setSalesDateTo(''); }}
                style={{
                  padding: '6px 14px', borderRadius: 5, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 12, transition: 'all 0.15s',
                  background: salesPeriod === p.id && !salesDateFrom ? 'var(--bg3)' : 'transparent',
                  color: salesPeriod === p.id && !salesDateFrom ? KA_GOLD : 'var(--text2)',
                  boxShadow: salesPeriod === p.id && !salesDateFrom ? '0 1px 4px rgba(30,36,37,0.1)' : 'none',
                }}
              >{p.label}</button>
            ))}
          </div>

          {/* Разделитель */}
          <div style={{ width: 1, height: 24, background: 'var(--border)', flexShrink: 0 }} />

          {/* Произвольный диапазон */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>с</span>
            <input type="date" value={salesDateFrom}
              onChange={e => { setSalesDateFrom(e.target.value); setSalesPeriod('custom'); }}
              style={{
                background: salesDateFrom ? 'rgba(0,167,220,0.08)' : 'var(--bg2)',
                border: `1px solid ${salesDateFrom ? 'rgba(0,167,220,0.4)' : 'var(--border)'}`,
                borderRadius: 6, color: 'var(--text1)', fontSize: 12,
                padding: '5px 8px', outline: 'none', cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--text2)' }}>по</span>
            <input type="date" value={salesDateTo}
              onChange={e => { setSalesDateTo(e.target.value); setSalesPeriod('custom'); }}
              style={{
                background: salesDateTo ? 'rgba(0,167,220,0.08)' : 'var(--bg2)',
                border: `1px solid ${salesDateTo ? 'rgba(0,167,220,0.4)' : 'var(--border)'}`,
                borderRadius: 6, color: 'var(--text1)', fontSize: 12,
                padding: '5px 8px', outline: 'none', cursor: 'pointer',
              }}
            />
          </div>

          {/* Кнопка Готово */}
          <button
            disabled={salesLoading}
            onClick={() => {
              if (salesDateFrom && salesDateTo) {
                loadSalesReport('custom', salesDateFrom, salesDateTo);
              } else {
                loadSalesReport(salesPeriod, '', '');
              }
            }}
            style={{
              marginLeft: 4,
              background: salesLoading ? 'var(--bg2)' : 'var(--accent)',
              color: salesLoading ? 'var(--text2)' : '#fff',
              border: 'none', borderRadius: 8,
              padding: '7px 20px', fontWeight: 700, fontSize: 13,
              cursor: salesLoading ? 'not-allowed' : 'pointer',
              boxShadow: salesLoading ? 'none' : 'none',
              transition: 'all 0.2s', whiteSpace: 'nowrap',
            }}
          >
            {salesLoading ? '⏳ Загрузка…' : '✓ Готово'}
          </button>

          {/* Разделитель */}
          <div style={{ width: 1, height: 24, background: 'var(--border)', flexShrink: 0 }} />

          {/* Тоглер: Без собственной розницы */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
            fontSize: 12, color: excludeOwnRetail ? '#0e9f6e' : 'var(--text2)',
            fontWeight: excludeOwnRetail ? 700 : 400,
            userSelect: 'none',
          }}>
            <div
              onClick={() => {
                const next = !excludeOwnRetail;
                setExcludeOwnRetail(next);
                if (salesData) {
                  if (salesDateFrom && salesDateTo) {
                    loadSalesReport('custom', salesDateFrom, salesDateTo, next);
                  } else {
                    loadSalesReport(salesPeriod, '', '', next);
                  }
                }
              }}
              style={{
                width: 34, height: 18, borderRadius: 9,
                background: excludeOwnRetail ? '#0e9f6e' : 'var(--bg2)',
                border: `1px solid ${excludeOwnRetail ? '#0e9f6e' : 'var(--border)'}`,
                position: 'relative', transition: 'all 0.2s', cursor: 'pointer', flexShrink: 0,
              }}
            >
              <div style={{
                position: 'absolute', top: 2,
                left: excludeOwnRetail ? 16 : 2,
                width: 12, height: 12, borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(30,36,37,0.075)',
              }} />
            </div>
            Без собств. розницы
          </label>
        </div>

        {/* Поиск по таблице */}
        {salesData?.items?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <input
              value={salesSearch}
              onChange={e => setSalesSearch(e.target.value)}
              placeholder="Поиск по артикулу, наименованию, покупателю..."
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 14px', background: 'var(--bg3)',
                border: '1px solid var(--border)', borderRadius: 8,
                color: 'var(--text1)', fontSize: 13, outline: 'none',
              }}
            />
          </div>
        )}

        {/* Состояния */}
        {!salesData && !salesLoading && (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            Выберите период и нажмите <b style={{ color: 'var(--text1)' }}>✓ Готово</b>
          </div>
        )}
        {salesLoading && (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
            Загружаем данные из 1С… (может занять 20–40 сек)
          </div>
        )}
        {salesData?.error && (
          <div style={{
            padding: 16, borderRadius: 8, marginBottom: 12,
            background: 'rgba(224,72,72,0.1)', border: '1px solid rgba(224,72,72,0.25)',
            color: '#e04848', fontSize: 13,
          }}>
            Ошибка: {salesData.error}
          </div>
        )}

        {/* Сводка */}
        {salesData?.items?.length > 0 && (() => {
          const items = salesData.items.filter(it => {
            const q = salesSearch.toLowerCase();
            return !q || (it.article || '').toLowerCase().includes(q)
              || (it.name || '').toLowerCase().includes(q)
              || (it.customer || '').toLowerCase().includes(q);
          });
          const totalRevenue = items.reduce((s, it) => s + it.revenue, 0);
          const totalCost    = items.reduce((s, it) => s + it.cost, 0);
          const totalQty     = items.reduce((s, it) => s + it.qty, 0);
          const margin       = totalRevenue - totalCost;

          return (
            <>
              {/* Мини-метрики */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'Выручка', value: `${Math.round(totalRevenue).toLocaleString('ru-RU')} ₽`, color: KA_BLUE },
                  { label: 'Себестоимость', value: `${Math.round(totalCost).toLocaleString('ru-RU')} ₽`, color: 'var(--text2)' },
                  { label: 'Маржа', value: `${Math.round(margin).toLocaleString('ru-RU')} ₽`, color: margin >= 0 ? 'var(--green)' : 'var(--red)' },
                  { label: 'Позиций', value: `${items.length}`, color: KA_GOLD },
                  { label: 'Штук', value: Math.round(totalQty).toLocaleString('ru-RU'), color: 'var(--text1)' },
                ].map(m => (
                  <div key={m.label} style={{
                    background: 'var(--bg3)', borderRadius: 8, padding: '10px 16px',
                    border: '1px solid var(--border)', minWidth: 120,
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{m.value}</div>
                  </div>
                ))}
              </div>

              {/* Таблица с группировкой */}
              {(() => {
                const isBobrov = it => (it.customer || '').toLowerCase().includes('бобров');
                const bobrovItems = items.filter(isBobrov);
                const otherItems  = items.filter(it => !isBobrov(it));

                const calcTotals = rows => {
                  const qty     = rows.reduce((s, r) => s + r.qty, 0);
                  const cost    = rows.reduce((s, r) => s + r.cost, 0);
                  const revenue = rows.reduce((s, r) => s + r.revenue, 0);
                  const margin  = revenue - cost;
                  const marginPct = revenue > 0 ? (margin / revenue * 100) : 0;
                  return { qty, cost, revenue, margin, marginPct };
                };

                const COL_STYLE = {
                  padding: '9px 12px', textAlign: 'left',
                  color: 'var(--text2)', fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                };

                const ItemRow = ({ it, i }) => {
                  const m  = it.margin  ?? (it.revenue - it.cost);
                  const mp = it.margin_pct ?? (it.revenue > 0 ? (m / it.revenue * 100) : 0);
                  const mc = m > 0 ? 'var(--green)' : m < 0 ? 'var(--red)' : 'var(--text2)';
                  return (
                    <tr style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '8px 12px', color: 'var(--text2)', fontSize: 11, minWidth: 32 }}>{i + 1}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {it.article
                          ? <span style={{ background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)', color: KA_GOLD, fontWeight: 800, fontSize: 11, padding: '2px 7px', borderRadius: 4, letterSpacing: '0.05em', fontFamily: 'monospace' }}>{it.article}</span>
                          : <span style={{ color: 'var(--text2)', fontSize: 11 }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 500, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.name}>{it.name}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--text1)', whiteSpace: 'nowrap' }}>{it.qty % 1 === 0 ? it.qty : it.qty.toFixed(2)} шт</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{it.cost > 0 ? `${Math.round(it.cost).toLocaleString('ru-RU')} ₽` : '—'}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text2)', fontSize: 12, whiteSpace: 'nowrap' }}>{it.cost_per_unit > 0 ? `${it.cost_per_unit.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽` : '—'}</td>
                      <td style={{ padding: '8px 12px', color: KA_BLUE, whiteSpace: 'nowrap' }}>{it.price_per_unit > 0 ? `${it.price_per_unit.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽` : '—'}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--green)', whiteSpace: 'nowrap' }}>{Math.round(it.revenue).toLocaleString('ru-RU')} ₽</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: mc, whiteSpace: 'nowrap' }}>{it.cost > 0 ? `${Math.round(m).toLocaleString('ru-RU')} ₽` : '—'}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {it.cost > 0
                          ? <span style={{ background: m > 0 ? 'rgba(14,159,110,0.15)' : 'rgba(224,72,72,0.15)', color: mc, padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{mp.toFixed(1)}%</span>
                          : <span style={{ color: 'var(--text2)', fontSize: 11 }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 12px', color: 'var(--text1)', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.customer}>{it.customer || '—'}</td>
                    </tr>
                  );
                };

                const TotalsRow = ({ rows, label }) => {
                  const t = calcTotals(rows);
                  const mc = t.margin > 0 ? 'var(--green)' : t.margin < 0 ? 'var(--red)' : 'var(--text2)';
                  return (
                    <tr style={{ borderTop: '2px solid var(--border)', background: 'rgba(0,167,220,0.07)' }}>
                      <td colSpan={3} style={{ padding: '9px 12px', fontWeight: 800, fontSize: 12, color: KA_GOLD, whiteSpace: 'nowrap' }}>
                        {label}
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 800, color: 'var(--text1)', whiteSpace: 'nowrap' }}>
                        {Math.round(t.qty).toLocaleString('ru-RU')} шт
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 800, color: 'var(--text1)', whiteSpace: 'nowrap' }}>
                        {Math.round(t.cost).toLocaleString('ru-RU')} ₽
                      </td>
                      <td style={{ padding: '9px 12px', color: 'var(--text2)' }}>—</td>
                      <td style={{ padding: '9px 12px', color: 'var(--text2)' }}>—</td>
                      <td style={{ padding: '9px 12px', fontWeight: 800, color: 'var(--green)', whiteSpace: 'nowrap' }}>
                        {Math.round(t.revenue).toLocaleString('ru-RU')} ₽
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 800, color: mc, whiteSpace: 'nowrap' }}>
                        {Math.round(t.margin).toLocaleString('ru-RU')} ₽
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ background: t.margin > 0 ? 'rgba(14,159,110,0.2)' : 'rgba(224,72,72,0.2)', color: mc, padding: '3px 9px', borderRadius: 5, fontSize: 12, fontWeight: 800 }}>
                          {t.marginPct.toFixed(1)}%
                        </span>
                      </td>
                      <td style={{ padding: '9px 12px', color: 'var(--text2)', fontSize: 11 }}>
                        {rows.length} поз.
                      </td>
                    </tr>
                  );
                };

                const GroupHeader = ({ label, color, icon }) => (
                  <tr style={{ background: color }}>
                    <td colSpan={11} style={{ padding: '8px 14px', fontWeight: 800, fontSize: 12, color: 'var(--text1)', letterSpacing: '0.03em' }}>
                      {icon} {label}
                    </td>
                  </tr>
                );

                const allTotals = calcTotals(items);
                const allMc = allTotals.margin > 0 ? 'var(--green)' : allTotals.margin < 0 ? 'var(--red)' : 'var(--text2)';

                return (
                  <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 2 }}>
                        <tr style={{ borderBottom: '2px solid var(--border)' }}>
                          {['#', 'Артикул', 'Наименование', 'Кол-во', 'С/с итого', 'С/с (ед)', 'Цена (ед)', 'Выручка', 'Маржа', '%', 'Кому реализовано'].map(h => (
                            <th key={h} style={COL_STYLE}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* ── Прочие покупатели ── */}
                        {otherItems.length > 0 && bobrovItems.length > 0 && (
                          <GroupHeader label="Прочие покупатели" color="rgba(100,120,160,0.35)" icon="👥" />
                        )}
                        {otherItems.map((it, i) => <ItemRow key={`o${i}`} it={it} i={i} />)}
                        {otherItems.length > 0 && (
                          <TotalsRow rows={otherItems} label={bobrovItems.length > 0 ? '↳ Итого прочие' : '↳ Итого'} />
                        )}

                        {/* ── ИП Бобров ── */}
                        {bobrovItems.length > 0 && (
                          <>
                            <GroupHeader label="ИП Бобров Илья Юрьевич" color="rgba(0,167,220,0.25)" icon="👤" />
                            {bobrovItems.map((it, i) => <ItemRow key={`b${i}`} it={it} i={i} />)}
                            <TotalsRow rows={bobrovItems} label="↳ Итого ИП Бобров" />
                          </>
                        )}

                        {/* ── Общий итог (если есть оба блока) ── */}
                        {otherItems.length > 0 && bobrovItems.length > 0 && (
                          <tr style={{ borderTop: '3px solid var(--border)', background: 'rgba(14,159,110,0.07)' }}>
                            <td colSpan={3} style={{ padding: '10px 14px', fontWeight: 900, fontSize: 13, color: 'var(--green)', whiteSpace: 'nowrap' }}>
                              Общий итог
                            </td>
                            <td style={{ padding: '10px 14px', fontWeight: 900, color: 'var(--text1)', whiteSpace: 'nowrap' }}>
                              {Math.round(allTotals.qty).toLocaleString('ru-RU')} шт
                            </td>
                            <td style={{ padding: '10px 14px', fontWeight: 900, color: 'var(--text1)', whiteSpace: 'nowrap' }}>
                              {Math.round(allTotals.cost).toLocaleString('ru-RU')} ₽
                            </td>
                            <td colSpan={2} style={{ padding: '10px 14px', color: 'var(--text2)' }}>—</td>
                            <td style={{ padding: '10px 14px', fontWeight: 900, color: 'var(--green)', whiteSpace: 'nowrap' }}>
                              {Math.round(allTotals.revenue).toLocaleString('ru-RU')} ₽
                            </td>
                            <td style={{ padding: '10px 14px', fontWeight: 900, color: allMc, whiteSpace: 'nowrap' }}>
                              {Math.round(allTotals.margin).toLocaleString('ru-RU')} ₽
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ background: allTotals.margin > 0 ? 'rgba(14,159,110,0.25)' : 'rgba(224,72,72,0.25)', color: allMc, padding: '3px 10px', borderRadius: 5, fontSize: 13, fontWeight: 900 }}>
                                {allTotals.marginPct.toFixed(1)}%
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--text2)', fontSize: 11 }}>
                              {items.length} поз.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {salesSearch && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)' }}>
                  Показано {items.length} из {salesData.items.length} позиций
                </div>
              )}
            </>
          );
        })()}

        {salesData && !salesLoading && salesData.items?.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            За выбранный период продаж не найдено
          </div>
        )}
      </ACard>

      {/* ── Списания товара ── */}
      <ACard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 20 }}>🧾</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Списания товара</span>
          {writeoffData?.label && (
            <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>
              — {writeoffData.label}
            </span>
          )}
          {writeoffData?.items?.length > 0 && (
            <button
              onClick={async () => {
                const blob = await exportFinDirectorWriteoffReportXlsx(
                  writeoffDateFrom && writeoffDateTo ? 'custom' : writeoffPeriod,
                  writeoffDateFrom,
                  writeoffDateTo,
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `списания_${writeoffData.date_from}_${writeoffData.date_to}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{
                marginLeft: 'auto',
                background: 'rgba(14,159,110,0.15)', border: '1px solid rgba(14,159,110,0.3)',
                color: 'var(--green)', padding: '6px 14px', borderRadius: 8,
                fontWeight: 700, fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              ⬇ Excel
            </button>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
          background: 'var(--bg3)', borderRadius: 10, padding: '10px 14px', flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', background: 'var(--bg2)', borderRadius: 7, padding: 2 }}>
            {[{ id: 'day', label: 'День' }, { id: 'week', label: 'Неделя' }, { id: 'month', label: 'Месяц' }].map(p => (
              <button
                key={p.id}
                onClick={() => { setWriteoffPeriod(p.id); setWriteoffDateFrom(''); setWriteoffDateTo(''); }}
                style={{
                  padding: '6px 14px', borderRadius: 5, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 12,
                  background: writeoffPeriod === p.id && !writeoffDateFrom ? 'var(--bg3)' : 'transparent',
                  color: writeoffPeriod === p.id && !writeoffDateFrom ? KA_GOLD : 'var(--text2)',
                  boxShadow: writeoffPeriod === p.id && !writeoffDateFrom ? '0 1px 4px rgba(30,36,37,0.1)' : 'none',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div style={{ width: 1, height: 24, background: 'var(--border)', flexShrink: 0 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>с</span>
            <input
              type="date"
              value={writeoffDateFrom}
              onChange={e => { setWriteoffDateFrom(e.target.value); setWriteoffPeriod('custom'); }}
              style={{
                background: writeoffDateFrom ? 'rgba(0,167,220,0.08)' : 'var(--bg2)',
                border: `1px solid ${writeoffDateFrom ? 'rgba(0,167,220,0.4)' : 'var(--border)'}`,
                borderRadius: 6, color: 'var(--text1)', fontSize: 12,
                padding: '5px 8px', outline: 'none', cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--text2)' }}>по</span>
            <input
              type="date"
              value={writeoffDateTo}
              onChange={e => { setWriteoffDateTo(e.target.value); setWriteoffPeriod('custom'); }}
              style={{
                background: writeoffDateTo ? 'rgba(0,167,220,0.08)' : 'var(--bg2)',
                border: `1px solid ${writeoffDateTo ? 'rgba(0,167,220,0.4)' : 'var(--border)'}`,
                borderRadius: 6, color: 'var(--text1)', fontSize: 12,
                padding: '5px 8px', outline: 'none', cursor: 'pointer',
              }}
            />
          </div>

          <button
            disabled={writeoffLoading}
            onClick={() => {
              if (writeoffDateFrom && writeoffDateTo) {
                loadWriteoffReport('custom', writeoffDateFrom, writeoffDateTo);
              } else {
                loadWriteoffReport(writeoffPeriod, '', '');
              }
            }}
            style={{
              background: writeoffLoading ? 'var(--bg2)' : 'var(--accent)',
              color: writeoffLoading ? 'var(--text2)' : '#fff',
              border: 'none', borderRadius: 8,
              padding: '7px 20px', fontWeight: 700, fontSize: 13,
              cursor: writeoffLoading ? 'not-allowed' : 'pointer',
              boxShadow: writeoffLoading ? 'none' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {writeoffLoading ? '⏳ Загрузка…' : '✓ Готово'}
          </button>
        </div>

        {writeoffData?.items?.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'Списано по закупке', value: fmt(writeoffData.total_cost), color: '#e04848' },
                { label: 'Списано по рознице', value: fmt(writeoffData.total_retail), color: KA_BLUE },
                { label: 'Позиций', value: `${writeoffData.count}`, color: KA_GOLD },
                { label: 'Документов', value: `${writeoffData.documents_count}`, color: 'var(--text1)' },
                { label: 'Штук', value: fmtN(writeoffData.total_qty), color: 'var(--text1)' },
              ].map(m => (
                <div key={m.label} style={{
                  background: 'var(--bg3)', borderRadius: 8, padding: '10px 16px',
                  border: '1px solid var(--border)', minWidth: 130,
                }}>
                  <div style={{ fontSize: 10, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{m.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{m.value}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 12 }}>
              <input
                value={writeoffSearch}
                onChange={e => setWriteoffSearch(e.target.value)}
                placeholder="Поиск по артикулу, товару, складу..."
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '8px 14px', background: 'var(--bg3)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  color: 'var(--text1)', fontSize: 13, outline: 'none',
                }}
              />
            </div>
          </>
        )}

        {!writeoffData && !writeoffLoading && (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            Выберите период и нажмите <b style={{ color: 'var(--text1)' }}>✓ Готово</b>
          </div>
        )}
        {writeoffLoading && (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
            Загружаем списания из 1С…
          </div>
        )}
        {writeoffData?.error && (
          <div style={{
            padding: 16, borderRadius: 8, marginBottom: 12,
            background: 'rgba(224,72,72,0.1)', border: '1px solid rgba(224,72,72,0.25)',
            color: '#e04848', fontSize: 13,
          }}>
            Ошибка: {writeoffData.error}
          </div>
        )}
        {writeoffData && !writeoffLoading && writeoffRows.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            За выбранный период списаний не найдено
          </div>
        )}

        {writeoffRows.length > 0 && (
          <>
            <div style={{
              fontSize: 12, color: 'var(--text2)', marginBottom: 10,
              padding: '10px 12px', background: 'rgba(224,138,0,0.06)',
              border: '1px solid rgba(224,138,0,0.15)', borderRadius: 8,
            }}>
              Суммы по товарам рассчитаны по текущим закупочным и розничным ценам 1С.
            </div>

            <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 2 }}>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    {[
                      { key: 'article', label: 'Артикул' },
                      { key: 'name', label: 'Наименование' },
                      { key: 'warehouse', label: 'Склад' },
                      { key: 'qty', label: 'Штук' },
                      { key: 'purchase_price', label: 'Закуп. цена' },
                      { key: 'retail_price', label: 'Розн. цена' },
                      { key: 'cost_total', label: 'Списано закуп.' },
                      { key: 'retail_total', label: 'Списано розн.' },
                      { key: 'doc_count', label: 'Документов' },
                      { key: 'last_date', label: 'Последняя дата' },
                    ].map(h => (
                      <th
                        key={h.key}
                        onClick={() => setWriteoffSort(prev => ({
                          key: h.key,
                          dir: prev.key === h.key && prev.dir === 'desc' ? 'asc' : 'desc',
                        }))}
                        style={{
                          padding: '9px 12px', textAlign: 'left', color: writeoffSort.key === h.key ? KA_BLUE : 'var(--text2)',
                          fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                          whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
                        }}
                      >
                        {h.label} {writeoffSort.key === h.key ? (writeoffSort.dir === 'desc' ? '↓' : '↑') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {writeoffRows.map((it, i) => (
                    <tr
                      key={`${it.article}-${it.name}-${it.warehouse}-${i}`}
                      style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {it.article
                          ? <span style={{ background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)', color: KA_GOLD, fontWeight: 800, fontSize: 11, padding: '2px 7px', borderRadius: 4, letterSpacing: '0.05em', fontFamily: 'monospace' }}>{it.article}</span>
                          : <span style={{ color: 'var(--text2)', fontSize: 11 }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 500, maxWidth: 360 }} title={it.name}>{it.name}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{it.warehouse || '—'}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 800, color: 'var(--text1)', whiteSpace: 'nowrap' }}>{fmtN(it.qty)}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{it.purchase_price ? `${it.purchase_price.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽` : '—'}</td>
                      <td style={{ padding: '8px 12px', color: KA_BLUE, whiteSpace: 'nowrap' }}>{it.retail_price ? `${it.retail_price.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽` : '—'}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 800, color: '#e04848', whiteSpace: 'nowrap' }}>{fmt(it.cost_total)}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: KA_BLUE, whiteSpace: 'nowrap' }}>{fmt(it.retail_total)}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text1)', whiteSpace: 'nowrap' }}>{it.doc_count}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{it.last_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </ACard>

      {/* ── Документы приёмки ── */}
      <ACard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 20 }}>📥</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Документы приёмки</span>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>
            Скачивание готовых актов приёмки поставок
          </span>
          <button
            onClick={loadReceipts}
            style={{
              marginLeft: 'auto',
              background: 'rgba(0,167,220,0.12)', border: '1px solid rgba(0,167,220,0.25)',
              color: KA_BLUE, padding: '6px 14px', borderRadius: 8,
              fontWeight: 700, fontSize: 12, cursor: 'pointer',
            }}
          >
            🔄 Обновить
          </button>
        </div>

        {receiptsLoading && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            Загружаем документы приёмки…
          </div>
        )}

        {!receiptsLoading && receipts.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
            Пока нет сохранённых документов приёмки
          </div>
        )}

        {!receiptsLoading && receipts.length > 0 && (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: 'var(--bg2)' }}>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['Документ', 'Поставщик', 'Позиций', 'Заказано', 'Получено', 'В продаже', 'Расхождений', 'Статус', 'Дата', 'Действия'].map(h => (
                    <th key={h} style={{
                      padding: '9px 12px', textAlign: 'left', color: 'var(--text2)',
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {receipts.map((r) => {
                  const detail = receiptDetails[r.id];
                  const isExpanded = expandedReceiptId === r.id;
                  const isLoadingDetail = !!receiptDetailLoading[r.id];
                  const itemRows = detail?.items || [];
                  const docLabel = r.doc_number || `#${r.id}`;
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '8px 12px', color: 'var(--text1)', fontWeight: 700, whiteSpace: 'nowrap' }}>{docLabel}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{r.supplier || '—'}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text1)' }}>{r.items_count ?? (r.items || []).length}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{fmt(r.total_ordered)}</td>
                        <td style={{ padding: '8px 12px', color: '#0e9f6e', fontWeight: 700 }}>{fmt(r.total_received)}</td>
                        <td style={{ padding: '8px 12px', color: KA_BLUE, fontWeight: 700 }}>{fmt(r.total_retail)}</td>
                        <td style={{ padding: '8px 12px', color: (r.discrepancies || []).length > 0 ? '#e08a00' : '#0e9f6e' }}>
                          {(r.discrepancies || []).length}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{
                            background: r.status === 'done' ? '#0e9f6e22' : r.status === 'partial' ? '#e08a0022' : '#64748b22',
                            color: r.status === 'done' ? '#0e9f6e' : r.status === 'partial' ? '#e08a00' : 'var(--text2)',
                            borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                          }}>{r.status}</span>
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--text2)', fontSize: 12 }}>
                          {fmtDate(r.created_at)}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleToggleReceipt(r.id)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                minWidth: 34, height: 32, borderRadius: 6, border: '1px solid rgba(0,167,220,0.3)',
                                background: 'rgba(0,167,220,0.12)', color: KA_BLUE, cursor: 'pointer', fontWeight: 800,
                              }}
                              title={isExpanded ? 'Свернуть состав документа' : 'Открыть состав документа'}
                            >
                              {isLoadingDetail ? '…' : isExpanded ? '▾' : '▸'}
                            </button>
                            <button
                              onClick={() => handleShareReceipt(r.id)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                minWidth: 34, height: 32, borderRadius: 6, border: '1px solid rgba(0,167,220,0.28)',
                                background: 'rgba(0,167,220,0.12)', color: KA_GOLD, cursor: 'pointer', fontWeight: 800,
                              }}
                              title="Скопировать ссылку на документ"
                            >
                              ↗
                            </button>
                            <button
                              onClick={async () => {
                                const blob = await exportProcurementReceiptXlsx(r.id);
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `приемка_${r.doc_number || r.id}.xlsx`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                color: '#fff', fontSize: 12, cursor: 'pointer',
                                padding: '4px 12px', border: 'none',
                                background: 'var(--accent)',
                                borderRadius: 6, fontWeight: 600,
                                boxShadow: '0 1px 4px rgba(30,36,37,0.075)',
                              }}
                              title="Скачать Excel"
                            >
                              📊 Excel
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,167,220,0.04)' }}>
                          <td colSpan={10} style={{ padding: '14px 16px 16px' }}>
                            {isLoadingDetail && (
                              <div style={{ color: 'var(--text2)', fontSize: 13 }}>Загружаем состав документа…</div>
                            )}
                            {!isLoadingDetail && (
                              <>
                                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
                                  <div><span style={{ color: 'var(--text2)' }}>Документ:</span> <span style={{ color: 'var(--text1)', fontWeight: 700 }}>{detail?.doc_number || docLabel}</span></div>
                                  <div><span style={{ color: 'var(--text2)' }}>Дата:</span> <span style={{ color: 'var(--text1)' }}>{fmtDate(detail?.created_at || r.created_at)}</span></div>
                                  <div><span style={{ color: 'var(--text2)' }}>Склад:</span> <span style={{ color: 'var(--text1)' }}>{detail?.warehouse_name || '—'}</span></div>
                                  <div><span style={{ color: 'var(--text2)' }}>Позиций:</span> <span style={{ color: 'var(--text1)' }}>{detail?.items_count ?? itemRows.length}</span></div>
                                  <div><span style={{ color: 'var(--text2)' }}>В продаже:</span> <span style={{ color: KA_BLUE, fontWeight: 700 }}>{fmt(detail?.total_retail ?? r.total_retail)}</span></div>
                                </div>
                                {detail?.notes && (
                                  <div style={{ marginBottom: 12, color: 'var(--text2)', fontSize: 12 }}>
                                    Примечание: <span style={{ color: 'var(--text1)' }}>{detail.notes}</span>
                                  </div>
                                )}
                                {itemRows.length === 0 ? (
                                  <div style={{ color: 'var(--text2)', fontSize: 13 }}>В этом документе нет строк товара.</div>
                                ) : (
                                  <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                      <thead style={{ background: 'rgba(30,36,37,0.03)' }}>
                                        <tr>
                                          {['SKU', 'Товар', 'Заказано', 'Получено', 'Закупка', 'Сумма закупки', 'Продажа', 'Сумма продажи'].map(h => (
                                            <th key={h} style={{
                                              padding: '8px 10px', textAlign: 'left', color: 'var(--text2)',
                                              fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                                            }}>{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {itemRows.map((item, idx) => (
                                          <tr key={`${r.id}-${idx}`} style={{ borderTop: '1px solid var(--border)' }}>
                                            <td style={{ padding: '8px 10px', color: 'var(--text1)', fontFamily: 'monospace' }}>{item.sku || '—'}</td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text1)', minWidth: 320 }}>{item.name || '—'}</td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text2)' }}>{fmtN(item.ordered_qty)}</td>
                                            <td style={{ padding: '8px 10px', color: '#0e9f6e', fontWeight: 700 }}>{fmtN(item.received_qty)}</td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text2)' }}>{fmt(item.purchase_price || item.price || 0)}</td>
                                            <td style={{ padding: '8px 10px', color: 'var(--text1)' }}>{fmt(item.amount || ((item.received_qty || 0) * (item.purchase_price || item.price || 0)))}</td>
                                            <td style={{ padding: '8px 10px', color: KA_BLUE }}>{item.sale_price ? fmt(item.sale_price) : '—'}</td>
                                            <td style={{ padding: '8px 10px', color: KA_BLUE, fontWeight: 700 }}>{item.retail_amount ? fmt(item.retail_amount) : '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ACard>

      {/* Зарплатная ведомость */}
      <ACard>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            💼 Зарплатная ведомость
            <span style={{ marginLeft: 10, fontSize: 12, color: KA_GOLD, fontWeight: 600 }}>
              ФОТ: {fmt(payroll?.total_accrued)}
            </span>
          </div>
          <input
            type="month" value={period}
            onChange={e => setPeriod(e.target.value)}
            style={{
              background: 'var(--bg3)', color: 'var(--text1)',
              border: '1px solid var(--border)', padding: '7px 12px',
              borderRadius: 7, fontSize: 13,
            }}
          />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Сотрудник', 'Начислено', 'Выплачено', 'Остаток'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payroll?.employees?.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 12px', fontWeight: 500 }}>{e.employee}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 700 }}>{fmt(e.accrued)}</td>
                  <td style={{ padding: '9px 12px', color: 'var(--green)', fontWeight: 600 }}>{fmt(e.paid)}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: e.accrued - e.paid > 0 ? 'var(--yellow)' : 'var(--text2)' }}>
                    {fmt(e.accrued - e.paid)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!payroll?.employees?.length && (
            <div style={{ padding: 24, color: 'var(--text2)', textAlign: 'center' }}>Нет данных</div>
          )}
        </div>
      </ACard>
    </div>
  );
}
